export type RawMessage = Record<string, unknown> & { _id: string; content?: string; createdAt?: string; isOptimistic?: boolean; failed?: boolean; clientKey?: string };
export type MessageHistory = { messages: RawMessage[]; [key: string]: unknown };

export const vendorMarketplaceKeys = {
  root: (accountId: string) => ["marketplace", "vendor", accountId] as const,
  chats: (accountId: string) => ["marketplace", "vendor", accountId, "chats"] as const,
  messages: (accountId: string, chatId: string) => ["marketplace", "vendor", accountId, "messages", chatId] as const,
  unread: (accountId: string) => ["marketplace", "vendor", accountId, "unread"] as const,
};

/** Keep socket preview updates that arrived while a conversation snapshot was loading. */
export const mergeConversationSnapshot = <T extends { _id: string; raw: Record<string, unknown> }>(
  beforeRequest: readonly T[] | undefined,
  current: readonly T[] | undefined,
  incoming: readonly T[],
  isAllowed: (conversation: T) => boolean,
): T[] => {
  const before = new Map(beforeRequest?.map((conversation) => [conversation._id, conversation]));
  const now = new Map(current?.map((conversation) => [conversation._id, conversation]));
  const incomingIds = new Set(incoming.map((conversation) => conversation._id));
  const result = incoming.filter(isAllowed).map((conversation) => {
    const previous = before.get(conversation._id);
    const live = now.get(conversation._id);
    if (!previous || !live || !isAllowed(live) || live.raw.lastMessage === previous.raw.lastMessage) return conversation;
    return { ...conversation, raw: { ...conversation.raw, lastMessage: live.raw.lastMessage, updatedAt: live.raw.updatedAt } };
  });
  // A newly created, verified conversation may arrive before the list endpoint catches up.
  // Rows present in the original snapshot remain subject to server-owned deletion.
  for (const conversation of current || []) {
    if (!before.has(conversation._id) && !incomingIds.has(conversation._id) && isAllowed(conversation)) result.push(conversation);
  }
  return result;
};

export const record = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
export const entityId = (value: unknown): string => {
  if (typeof value === "string") return value;
  const item = record(value);
  return typeof item?._id === "string" ? item._id : typeof item?.id === "string" ? item.id : "";
};
export const messageChatId = (message: unknown): string => {
  const item = record(message);
  return entityId(item?.chatId) || entityId(item?.chat);
};
export const readMessage = (value: unknown): RawMessage | null => {
  const item = record(value);
  if (!item || !entityId(item)) return null;
  return { ...item, _id: entityId(item) } as RawMessage;
};
export const readAcknowledgement = (value: unknown): RawMessage | null => {
  const item = record(value);
  const data = record(item?.data);
  for (const candidate of [item?.message, item?.updated, data?.message, data, item]) {
    const message = readMessage(candidate);
    if (message && (typeof message.content === "string" || Array.isArray(message.files) || Array.isArray(message.images) || Boolean(message.video) || Array.isArray(message.documents))) return message;
  }
  return null;
};
export const readHistory = (value: unknown): MessageHistory => {
  const item = record(value);
  const messages = Array.isArray(value) ? value : item?.messages;
  if (!Array.isArray(messages)) throw new Error("The message history response is missing its messages list.");
  return { ...item, messages: sortMessages(messages.flatMap((m) => { const parsed = readMessage(m); return parsed ? [parsed] : []; })) };
};
export const sortMessages = (messages: RawMessage[]): RawMessage[] => {
  const unique = new Map<string, RawMessage>();
  for (const message of messages) unique.set(message._id, { ...unique.get(message._id), ...message });
  return [...unique.values()].sort((a, b) => {
    const left = Date.parse(a.createdAt || "");
    const right = Date.parse(b.createdAt || "");
    return Number.isFinite(left) && Number.isFinite(right) ? left - right : 0;
  });
};

/** Only a request's own acknowledgement may replace its optimistic entry. */
export const mergeMessage = (history: MessageHistory | undefined, incoming: RawMessage, optimisticId?: string): MessageHistory => {
  const current = history?.messages || [];
  const optimistic = optimisticId ? current.find((message) => message._id === optimisticId) : undefined;
  const existing = current.find((message) => message._id === incoming._id);
  const merged = {
    ...optimistic,
    ...existing,
    ...incoming,
    sender: incoming.sender ?? existing?.sender ?? optimistic?.sender,
    replyTo: incoming.replyTo ?? existing?.replyTo ?? optimistic?.replyTo,
    clientKey: optimistic?.clientKey || optimistic?._id || existing?.clientKey || incoming._id,
    isOptimistic: Boolean(incoming.isOptimistic),
    failed: Boolean(incoming.failed),
  };
  return { ...history, messages: sortMessages([...current.filter((message) => message._id !== incoming._id && message._id !== optimisticId), merged]) };
};

/** A late history response must preserve live events and in-flight sends. */
export const mergeHistory = (current: MessageHistory | undefined, incoming: MessageHistory, beforeRequest?: MessageHistory): MessageHistory => {
  const before = new Map(beforeRequest?.messages.map((message) => [message._id, message]));
  const now = new Map(current?.messages.map((message) => [message._id, message]));
  const changedDuringRequest = new Set([...now].filter(([id, message]) => before.has(id) && before.get(id) !== message).map(([id]) => id));
  const deletedDuringRequest = new Set([...before.keys()].filter((id) => !now.has(id)));
  const preserved = current?.messages.filter((message) => message.isOptimistic || message.failed || !before.has(message._id) || changedDuringRequest.has(message._id)) || [];
  let result: MessageHistory = { ...incoming, messages: preserved };
  for (const message of incoming.messages) {
    if (!deletedDuringRequest.has(message._id) && !changedDuringRequest.has(message._id)) result = mergeMessage(result, message);
  }
  return result;
};

/** List previews can omit an author in the existing API's lastMessage summary. */
export const validChatPreview = (value: unknown, chatId: string, participants: readonly string[]): boolean => {
  const message = record(value);
  if (!message || message.isCustomerCareChat === true || message.isDeleted === true || (message.deletedFor && message.deletedFor !== "none")) return false;
  const room = messageChatId(message);
  const sender = entityId(message.sender) || entityId(message.senderId) || entityId(message.from);
  return (!room || room === chatId) && (!sender || participants.includes(sender));
};

export const messageBelongsToChat = (value: unknown, chatId: string, participants: readonly string[]): boolean => {
  const message = record(value);
  if (!message || message.isCustomerCareChat === true || message.isDeleted === true || (message.deletedFor && message.deletedFor !== "none")) return false;
  const room = messageChatId(message);
  const sender = entityId(message.sender) || entityId(message.senderId) || entityId(message.from);
  return (!room || room === chatId) && Boolean(sender && participants.includes(sender));
};

export const failMessage = (history: MessageHistory | undefined, optimisticId: string): MessageHistory | undefined =>
  history ? { ...history, messages: history.messages.map((message) => message._id === optimisticId ? { ...message, isOptimistic: false, failed: true } : message) } : history;

export const rollbackEdit = (history: MessageHistory | undefined, previous: RawMessage | undefined, attemptedText: string): MessageHistory | undefined =>
  history && previous ? { ...history, messages: history.messages.map((message) => message._id === previous._id && message.content === attemptedText ? { ...message, content: previous.content, isEdited: previous.isEdited } : message) } : history;

export const messagePreview = (value: unknown): string => {
  const message = record(value);
  if (typeof message?.content === "string" && message.content.trim()) return message.content;
  const files = Array.isArray(message?.files) ? message.files.map(record) : [];
  if (files.some((file) => String(file?.type || file?.mimetype || "").startsWith("image")) || (Array.isArray(message?.images) && message.images.length)) return "Photo";
  if (files.some((file) => String(file?.type || file?.mimetype || "").startsWith("video")) || message?.video) return "Video";
  if (files.length || (Array.isArray(message?.documents) && message.documents.length)) return "Document";
  return "No messages yet";
};

export const dateLabel = (timestamp: string): string => {
  const date = new Date(timestamp);
  if (!Number.isFinite(date.getTime())) return "";
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { day: "numeric", month: "short", year: date.getFullYear() === today.getFullYear() ? undefined : "numeric" });
};
